import { useQuery } from '@tanstack/react-query'
import { getAllReturnReasons, saveReturnReasons } from '@/api/settings-api'
import { useEcommerceId } from '@/hooks/use-ecommerce-id'
import { ReturnReason } from '@/domain/return-reasons'
import {
  useSettingsDraft,
  useSettingsMutation
} from '@/application/manage-settings'
import { toBackendReasons, toReturnReasons } from './return-reasons'

const RETURN_REASONS_KEY = ['returnReasons']

const returnReasonsKey = (ecommerceId?: string) => [
  ...RETURN_REASONS_KEY,
  ecommerceId
]

const NO_REASONS: ReturnReason[] = []

export function useReturnReasons() {
  const ecommerceId = useEcommerceId()
  const query = useQuery({
    queryKey: returnReasonsKey(ecommerceId),
    queryFn: () => getAllReturnReasons(ecommerceId ?? ''),
    enabled: !!ecommerceId,
    select: toReturnReasons
  })
  const [returnReasons, setReturnReasons] = useSettingsDraft(
    query.data,
    NO_REASONS
  )

  return {
    returnReasons,
    setReturnReasons,
    originalReturnReasons: query.data ?? NO_REASONS,
    loadingReturnReasons: query.isLoading,
    returnReasonsFailed: query.isError && !query.data,
    reloadReturnReasons: query.refetch
  }
}

// SaveReturnReasons replaces the whole set: the loaded-reasons guard in save keeps the others.
export function useSaveReturnReasons() {
  const ecommerceId = useEcommerceId()
  const { save, saving } = useSettingsMutation({
    mutationFn: (reasons: ReturnReason[]) =>
      saveReturnReasons(ecommerceId ?? '', toBackendReasons(reasons)),
    invalidate: returnReasonsKey(ecommerceId),
    successKey: 'return_reasons.save_success',
    errorKey: 'return_reasons.save_error'
  })

  return {
    callSaveReturnReasons: save,
    loadingSaveReturnReasons: saving
  }
}
